import {z} from 'zod';
import {DateTime} from 'luxon';
import {ConditionOperatorKey} from 'common/src/modules/conditions/conditions.types';
import {CompoundConditionNodeValue} from 'common/src/modules/decisionTree/decisionTree.types';
import {conditionOutputSchema} from '../../promptToCardAgentV2.schemas';

/**
 * Validates and converts raw rule values (from LLM input) into CompoundConditionNodeValue,
 * handling date string to DateTime conversion.
 * @param rawValue - The raw value from the schema input
 * @param ruleOperator - The operator for this rule (affects range vs array logic)
 * @returns The converted CompoundConditionNodeValue or an error string
 */
export default function setRuleValue(
  rawValue: z.infer<typeof conditionOutputSchema>,
  ruleOperator: ConditionOperatorKey,
): CompoundConditionNodeValue | string {
  const value: CompoundConditionNodeValue = {};

  if (rawValue.textValues) {
    value.textValues = rawValue.textValues;
  }

  if (rawValue.numericValues) {
    value.numericValues = rawValue.numericValues;
  }

  if (rawValue.booleanValue !== undefined && rawValue.booleanValue !== null) {
    value.booleanValue = rawValue.booleanValue;
  }

  if (rawValue.dateValues) {
    const parsedDates = rawValue.dateValues.map((date) => DateTime.fromISO(date));
    const invalidDate = parsedDates.find((d) => !d.isValid);

    if (invalidDate) {
      return `Invalid date value: "${invalidDate.invalidExplanation}"`;
    }

    value.dateValues = parsedDates;
  }

  if (rawValue.datesRange) {
    const from = DateTime.fromISO(rawValue.datesRange.from);
    const to = DateTime.fromISO(rawValue.datesRange.to);

    if (!from.isValid) {
      return `Invalid "from" date in datesRange: "${from.invalidExplanation}"`;
    }

    if (!to.isValid) {
      return `Invalid "to" date in datesRange: "${to.invalidExplanation}"`;
    }

    value.datesRange = {from, to};
  }

  if (rawValue.numbersRange) {
    value.numbersRange = rawValue.numbersRange
      ? {
          from: rawValue.numbersRange.from,
          to: rawValue.numbersRange.to,
        }
      : undefined;
  }

  const hasAnyValue =
    value.textValues ||
    value.numericValues ||
    value.booleanValue !== undefined ||
    value.dateValues ||
    value.datesRange ||
    value.numbersRange;

  if (!hasAnyValue && ruleOperator !== ConditionOperatorKey.Exists) {
    return 'At least one value field must be provided for the rule';
  }

  return value;
}
